/**
 * @fileoverview Quick Room Create Modal
 * Template-based room creation with positioning
 * @author Hospital Room Scheduler Team
 * @created 2025-10-15 23:35:00
 */

import React, { useState } from 'react';
import { Modal, Stack, Text, Button, Group, NumberInput, TextInput, MultiSelect } from '@mantine/core';
import { RoomTemplateSelector } from '../molecules/RoomTemplateSelector';
import { useCreateRoomWithPosition } from '../../hooks/admin/useRooms';
import { useSessionTemplates } from '../../hooks/api/useSessionTemplates';
import type { RoomTemplate } from '@shared/types/room-template';

interface QuickRoomCreateModalProps {
  floorId: string;
  opened: boolean;
  onClose: () => void;
}

/**
 * Quick Room Create Modal - Create room from template
 */
export const QuickRoomCreateModal: React.FC<QuickRoomCreateModalProps> = ({
  floorId,
  opened,
  onClose
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState<RoomTemplate | null>(null);
  const [roomNumber, setRoomNumber] = useState('');
  const [roomName, setRoomName] = useState('');
  const [canvasX, setCanvasX] = useState(100);
  const [canvasY, setCanvasY] = useState(100);
  const [sessionTemplateIds, setSessionTemplateIds] = useState<string[]>([]);

  const createMutation = useCreateRoomWithPosition();
  const { data: sessionTemplatesData, isLoading: sessionTemplatesLoading } = useSessionTemplates();

  const handleCreate = async () => {
    if (!selectedTemplate || !roomNumber || !roomName) return;

    try {
      await createMutation.mutateAsync({
        floorId,
        room: {
          number: roomNumber,
          name: roomName,
          capacity: selectedTemplate.defaultCapacity,
          templateId: selectedTemplate.id
          // Note: sessionTemplateIds is not supported by the current API
          // Would need backend changes to support this field
        },
        position: {
          x: canvasX,
          y: canvasY,
          width: selectedTemplate.defaultWidth,
          height: selectedTemplate.defaultHeight
        }
      });

      // Reset form
      setSelectedTemplate(null);
      setRoomNumber('');
      setRoomName('');
      setCanvasX(100);
      setCanvasY(100);
      setSessionTemplateIds([]);
      
      onClose();
    } catch (error: any) {
      // Error already handled by mutation hook with notifications
      // Just log for debugging
      console.error('Failed to create room:', error.message || error);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Create Room from Template"
      size="lg"
      centered
      zIndex={1200}
      overlayProps={{
        backgroundOpacity: 0.55,
        blur: 3
      }}
      styles={{
        inner: { zIndex: 1200 },
        content: { zIndex: 1201 }
      }}
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Select a template and provide room details. The room will be created and positioned on the canvas.
        </Text>

        <RoomTemplateSelector
          selectedTemplate={selectedTemplate}
          onTemplateSelect={setSelectedTemplate}
          compact={true}
          showFilters={false}
        />

        {selectedTemplate && (
          <>
            <TextInput
              label="Room Number"
              placeholder="e.g., 101"
              value={roomNumber}
              onChange={(e) => setRoomNumber(e.target.value)}
              required
            />
            <TextInput
              label="Room Name"
              placeholder="e.g., ICU Room A"
              value={roomName}
              onChange={(e) => setRoomName(e.target.value)}
              required
            />
            {/* TODO: Session Templates - Backend API needs to be updated to support sessionTemplateIds
            <MultiSelect
              label="Session Templates"
              placeholder="Select session templates (optional)"
              description="Which session times are available for this room"
              data={sessionTemplatesData?.data?.map((template: any) => ({
                value: template.id,
                label: `${template.name} (${template.startTime} - ${template.endTime})`
              })) || []}
              value={sessionTemplateIds}
              onChange={setSessionTemplateIds}
              searchable
              clearable
              disabled={sessionTemplatesLoading}
            />
            */}

            <Group grow>
              <NumberInput
                label="Canvas X Position"
                value={canvasX}
                onChange={(val) => setCanvasX(Number(val))}
                min={0}
              />
              <NumberInput
                label="Canvas Y Position"
                value={canvasY}
                onChange={(val) => setCanvasY(Number(val))}
                min={0}
              />
            </Group>

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={onClose}>
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                loading={createMutation.isPending}
                disabled={!roomNumber || !roomName}
              >
                Create Room
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
};

